<script>
    // Licensed under Gnu Public Licence version 2
    // Copyright (c) 2019 (original work) Open Assessment Technologies SA ;
    import { createEventDispatcher, tick } from 'svelte';
    import { __, getActualKey } from '@oat-sa-private/ui-core';
    import { Icon } from '@oat-sa-private/ui-elements';

    export let name;
    export let items = [];
    export let selected;
    export let showSelectionStatus = false;
    export let disabled = false;

    const dispatch = createEventDispatcher();
    let ariaLabels = [];

    $: hasSelection = typeof selected !== 'undefined';
    $: {
        items.forEach((item, index) => {
            // labels should update depending on the item score state: selected|selected by an other scorer|rejected
            ariaLabels[index] = getAriaLabel(item, selected);
        });
    }

    /**
     * Gets the `aria-attribute` label value depending on the scoring input element state
     *
     * @param {Object} item
     * @param {String} item.label
     * @param {Boolean} item.marked
     * @param {String} item.value
     * @param {String} selectedValue
     * @returns {String} aria-attribute` label value
     */
    function getAriaLabel(item, selectedValue) {
        let labels = [`Score ${item.label}.`];

        // Selected by an other scorer
        if (item.marked && typeof selectedValue === 'undefined') {
            labels.push(__('Selected by another scorer.'));
        }

        // Rejected
        if (item.marked && typeof selectedValue !== 'undefined' && selectedValue !== item.value) {
            labels.push(__('Rejected.'));
        }

        return labels.join(' ');
    }

    /**
     * Handles `click` events
     *
     * @param {Event} event
     */
    function onClick(event) {
        if (event.target.value === selected && event.target.checked) {
            selected = void 0;
        } else {
            selected = event.target.value;
        }
        tick().then(() => dispatch('change', { value: selected }));
        event.target.focus();
    }

    /**
     * Handles `keydown` events
     *
     * @param {KeyboardEvent} event
     */
    function onKeydown(event) {
        switch (getActualKey(event)) {
            case 'space':
            case 'enter':
                keydownSelectHandler(event);
                break;
            case 'left':
            case 'up':
            case 'right':
            case 'down':
                keydownArrowsHandler(event);
                break;
            case 'tab':
                keydownTabHandler(event);
                break;
            default:
                break;
        }
    }

    /**
     * Processing `Enter` and `Space` keys of the keydown events for select/deselecting the score.
     *
     * @param {KeyboardEvent} event
     */
    function keydownSelectHandler(event) {
        const { target: radioInput } = event;

        event.preventDefault();

        if (radioInput.value === selected && radioInput.checked) {
            selected = void 0;
        } else {
            selected = radioInput.value;
        }

        tick().then(() => dispatch('change', { value: selected }));
    }

    /**
     * Processing Arrow keys to move to the prev|next item without changing its state.
     *
     * @param {KeyboardEvent} event
     */
    function keydownArrowsHandler(event) {
        const radioLabel = event.target.parentElement;
        const keyPressed = getActualKey(event);
        const isLtr = document.dir !== 'rtl';

        event.preventDefault();

        if ((isLtr && ['left', 'up'].includes(keyPressed)) || (!isLtr && ['right', 'down'].includes(keyPressed))) {
            radioLabel.previousElementSibling && radioLabel.previousElementSibling.focus();
        } else {
            radioLabel.nextElementSibling && radioLabel.nextElementSibling.focus();
        }
    }

    /**
     * Processing `Tab` key.
     * Prevents `Mozilla Firefox` from using Tab for navigation between scores.
     *
     * @param {KeyboardEvent} event
     */
    function keydownTabHandler(event) {
        const radioLabel = event.target.parentElement;
        let targetElement;

        if (hasSelection) {
            if (event.target.value !== selected) {
                targetElement = Array.from(radioLabel.parentElement.children).filter(
                    child => child.innerText.trim() === selected
                );
                if (Array.isArray(targetElement) && targetElement.length > 0) {
                    targetElement[0].focus();
                }
            }
            return;
        }

        if (event.shiftKey) {
            targetElement = radioLabel.parentElement.firstElementChild;
        } else {
            targetElement = radioLabel.parentElement.lastElementChild;
        }

        targetElement.focus();
    }
</script>

<style>
    .container {
        --scoring-input-size: 5.75rem;

        display: flex;
        flex-wrap: wrap;

        & label {
            flex: 0 0 auto;
            display: block;
            box-sizing: border-box;
            border: var(--border-medium) solid var(--color-border-default);
            background-color: var(--color-bg-default);
            line-height: 5.25rem;
            height: var(--scoring-input-size);
            width: var(--scoring-input-size);
            text-align: center;
            font-weight: bold;
            margin: calc(-1 * var(--border-medium)) calc(-1 * var(--border-medium)) 0 0;
            cursor: pointer;
            position: relative;

            &:hover:not(.disabled),
            &:focus-within:not(.disabled) {
                background-color: var(--color-gs-light-alternative-bg);
            }

            &:focus-within:before {
                content: '';
                position: absolute;
                top: var(--space-quarter);
                left: var(--space-quarter);
                width: calc(100% - 2 * var(--space-quarter));
                height: calc(100% - 2 * var(--space-quarter));
                border: var(--border-medium) dotted var(--color-border-default);
            }

            &.disabled,
            &.disabled:hover {
                border-color: var(--color-gs-disabled);
                color: var(--color-gs-disabled);
                cursor: not-allowed;
            }

            &.marked {
                &:after {
                    content: '';
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                    height: 4rem;
                    width: 4rem;
                    border-radius: 50%;
                    border: var(--border-medium) solid;
                    border-color: var(--color-text-default);
                }

                &:focus-within:before {
                    border: var(--border-medium) dotted var(--color-text-default);
                }

                &.disabled:after {
                    border-color: var(--color-gs-disabled);
                }
            }

            &.rejected {
                &:after {
                    content: '';
                    display: block;
                    position: absolute;
                    top: 50%;
                    width: 100%;
                    transform: rotate(45deg);
                    border-top: var(--border-medium) solid var(--color-text-default);
                }

                &.disabled:after {
                    border-color: var(--color-gs-disabled);
                }

                &:not(.disabled):hover:after {
                    content: none;
                }
            }

            &.selected {
                background-color: var(--color-bg-actionable);
                color: var(--color-text-inverted);

                &.disabled:hover {
                    color: var(--color-text-inverted);
                }

                &:not(.disabled):hover,
                &:not(.disabled):focus-within {
                    background-color: var(--color-bg-actionable-hover);
                }

                &:not(.disabled):focus-within:before {
                    border: var(--border-medium) dotted var(--color-text-inverted);
                }
            }
        }

        & input {
            opacity: 0;
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);

            &:hover {
                cursor: pointer;
            }

            &:disabled,
            &:disabled:hover {
                cursor: not-allowed;
            }
        }

        & .selection-status {
            height: var(--scoring-input-size);
            width: var(--scoring-input-size);
            display: flex;
            align-items: center;
            justify-content: center;
            margin: calc(-1 * var(--border-medium)) calc(-1 * var(--border-medium)) 0 0;

            & .checkmark {
                color: var(--color-gs-disabled);
            }

            & .double-checkmark {
                position: relative;
                color: var(--color-brand);
            }
        }
    }
</style>

<div class="container" role="application" aria-roledescription={__('Scoring input')}>
    {#each items as item, i}
        <label
            class:selected={selected === item.value}
            class:marked={item.marked && !hasSelection}
            class:rejected={item.marked && hasSelection && selected !== item.value}
            class:disabled>
            {item.label}
            <input
                aria-label={ariaLabels[i]}
                aria-disabled={disabled || null}
                type="radio"
                {name}
                value={item.value}
                {disabled}
                bind:group={selected}
                on:click={onClick}
                on:keydown={onKeydown}
                on:keyup|preventDefault />
        </label>
    {/each}
    {#if showSelectionStatus}
        <div class="selection-status">
            {#if hasSelection}
                <div class="double-checkmark">
                    <Icon name="double-check-24" />
                </div>
            {:else}
                <div class="checkmark">
                    <Icon name="check-16" />
                </div>
            {/if}
        </div>
    {/if}
</div>
